import { useCallback } from "react";
import { IPaymentV2ScreenRouteProps } from "../PaymentScreen";
import { useRouter } from "next/router";
import { useGeneratePath } from "../../../hooks";
import { dispatch, PaymentStore } from "@nf/common";
import { ROUTES } from "@nf/constants";
import { useDataPayment } from "./useDataPayment";
import { useCreatePaymentCallback } from "../../../hooks/useCreatePaymentCallback";

export const useCreateRoute = () => {
  const router = useRouter();
  const { offerId: routerOfferId, voucherCode: routerVoucherCode } =
    router.query as IPaymentV2ScreenRouteProps;
  const generatePath = useGeneratePath();

  return useCallback(
    (route, offerId = routerOfferId, voucherCode = routerVoucherCode) => {
      const query: Record<string, string | number> = {};

      if (!!offerId) {
        query.offerId = offerId;
      }
      if (!!voucherCode) {
        query.voucherCode = encodeURIComponent(voucherCode);
      }
      return {
        pathname: generatePath(route),
        query,
      };
    },
    [routerVoucherCode, routerOfferId, generatePath]
  );
};

export const useCreatePaymentOrder = () => {
  const router = useRouter();

  const urlParams = new URLSearchParams(router.asPath);
  const voucherCodeParam = urlParams.get("voucherCode") ?? undefined;
  const { methods } = useDataPayment();
  const { getInitialPaymentCallbackUrl } = useCreatePaymentCallback();

  return useCallback(
    (offerId: string, voucherCode?: string) => {
      if (methods?.[0]?.Id && offerId) {
        const { okUrl, failUrl } = getInitialPaymentCallbackUrl(
          offerId,
          voucherCode
        );

        dispatch(PaymentStore.Actions.setPaymentCallback(okUrl, failUrl));
        dispatch(
          PaymentStore.Actions.createPaymentOrder({
            OfferId: offerId,
            CouponCode: voucherCode ?? voucherCodeParam,
            PaymentMethodId: methods[0].Id,
          })
        );
      }
    },
    [methods?.[0]?.Id, voucherCodeParam]
  );
};

export const useSelectOffer = () => {
  const router = useRouter();
  const createRoute = useCreateRoute();
  const { voucherCode } = router.query as IPaymentV2ScreenRouteProps;
  return useCallback(
    (offerId) => {
      setTimeout(() => {
        router.replace(
          createRoute(ROUTES.PAYMENT_SUBSCRIPTION, offerId, voucherCode),
          undefined,
          { shallow: true }
        );
      }, 500);
    },
    [router, voucherCode, createRoute]
  );
};

export const useHandleRedeem = () => {
  const router = useRouter();
  const createRoute = useCreateRoute();
  const createPaymentOrder = useCreatePaymentOrder();
  return useCallback(
    (offerId, voucherCode = "") => {
      router.replace(createRoute(ROUTES.PAYMENT_DETAILS, offerId, voucherCode));
    },
    [createRoute, router, createPaymentOrder]
  );
};

export const useRedirectToPaymentDetails = () => {
  const router = useRouter();
  const createRoute = useCreateRoute();
  return useCallback(
    (offerId: string, voucherCode?: string) => {
      router.replace(createRoute(ROUTES.PAYMENT_DETAILS, offerId, voucherCode));
    },
    [createRoute, router]
  );
};

const useIsTheSameOfferId = () => {
  const { paymentOrder } = useDataPayment();
  return useCallback(
    (offerId?: string | number) =>
      offerId?.toString() === paymentOrder?.OfferId.toString(),
    [paymentOrder?.OfferId]
  );
};

export const useBuildOfferIdPathname = () => {
  const generatePath = useGeneratePath();
  return useCallback(
    (route: string, offerId?: string) => ({
      pathname: generatePath(route),
      query: {
        offerId,
      },
    }),
    [generatePath]
  );
};

export const useHandleVoucher = () => {
  const router = useRouter();
  const createRoute = useCreateRoute();
  const { offersList } = useDataPayment();
  const { offerId, voucherCode } = router.query as IPaymentV2ScreenRouteProps;
  const buildOfferIdRoute = useBuildOfferIdPathname();

  return useCallback(() => {
    const monthlyOfferId = offersList?.filter(
      (offer) => offer.Period === "month"
    )?.[0].Id;

    const route = buildOfferIdRoute(
      ROUTES.PAYMENT_VOUCHER,
      monthlyOfferId ?? offerId
    );
    router.push(route, undefined, { shallow: false });
  }, [
    offerId,
    createRoute,
    router,
    voucherCode,
    buildOfferIdRoute,
    offersList,
  ]);
};

export const useHandlePayment = () => {
  const router = useRouter();
  const createRoute = useCreateRoute();
  const isTheSameOfferId = useIsTheSameOfferId();
  const buildOfferIdRoute = useBuildOfferIdPathname();
  const createPaymentOrder = useCreatePaymentOrder();
  return useCallback(
    (offerId) => {
      const route = isTheSameOfferId(offerId)
        ? createRoute(ROUTES.PAYMENT_DETAILS, offerId)
        : buildOfferIdRoute(ROUTES.PAYMENT_DETAILS, offerId);

      router.push(route, undefined, { shallow: true }).then(() => {
        createPaymentOrder(offerId);
      });
    },
    [createRoute, router, isTheSameOfferId, createPaymentOrder]
  );
};
